import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolWarning,
} from "../types";

/**
 * Corifollitropin alfa — one injection that does the first week's work.
 *
 * Every stimulation injection before this is daily. This one is given once, on
 * stimulation day 1, and replaces the first seven days of FSH; the label then
 * forbids a second one in the same cycle because of the OHSS risk. So the
 * failure mode is unique in the catalogue and it is not a technique error: a
 * patient who injects it again the next day, by habit or because nothing
 * seemed to happen. The walk says "once" three times — on the first card, on
 * the last, and in a caution — because the device gives her no other cue.
 *
 * ### The device
 *
 * A prefilled syringe with the needle packed separately: she unscrews a grey
 * cap, screws the needle on, pushes a droplet out, and after the injection the
 * needle retracts into the barrel on its own. Two of those steps are the kind a
 * coach exists for. The droplet is the only check that the path is primed, and
 * nothing afterwards tells her if she skipped it. The retraction means the
 * step every other walk ends with — "withdraw the needle" — would have her
 * pulling on a syringe whose needle is already gone.
 *
 * ### Market
 *
 * EMA-authorised and not an FDA product, so there is one leaflet and one
 * procedure. The brand is the only name it is sold under anywhere the content
 * lists it.
 *
 * ### Tiers
 *
 * Read against the EMA product information, English and French, in October
 * 2026 — see `docs/features/injection-coach/medications/elonva.md` for each
 * step's tier. Everything here is label text except two conventions carried
 * from the rest of the catalogue: pressure with gauze afterwards, and the
 * sharps container (the leaflet says "not household waste, ask your
 * pharmacist").
 *
 * Restated from `content/data/medications/gonadotropins.json` (the `Elonva`
 * entry), which was rewritten against the same leaflet first — a protocol may
 * omit from the approved content but never add to it.
 */

const ELONVA_BRANDS = [
  {
    name: "Elonva",
    markets: ["EU", "GB", "CH", "AU", "NZ", "CA", "SG", "JP", "HK"],
  },
] as const satisfies readonly ProtocolBrand[];

/**
 * Said on the first card, where it can still change what she does.
 *
 * The leaflet's own shape, and not the two-hour cliff the daily gonadotropins
 * once carried: there is no window, because there is no next dose to measure
 * against. Contact the clinic, and do not inject until they have answered.
 */
const MISSED_DOSE: ProtocolWarning = {
  id: "elonva.missed-dose",
  severity: "caution",
  // The sentence is the action; a caution never pauses the walk, so an
  // escalation on it would promise a destination the session never reaches.
  escalation: "none",
  copy: {
    en: "Missed dose - If you forgot to inject on the day you should have, contact your clinic immediately. Do not inject without talking with them first.",
    fr: "Dose oubliée - Si vous avez oublié l'injection le jour prévu, contactez immédiatement votre centre. N'injectez pas sans en avoir parlé avec eux.",
  },
  sourceSegment:
    "⚠ Missed dose - If you forgot to inject on the day you should have, contact your clinic immediately. Do not inject without talking with them first.",
};

/**
 * The rule no other procedure has, in the leaflet's words.
 *
 * On the last card rather than the first: the first card already says it as
 * part of what the medication is, and this is the instruction for the seven
 * days that follow, said at the moment they start.
 */
const ONLY_ONCE_PER_CYCLE: ProtocolWarning = {
  id: "elonva.only-once-per-cycle",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Only once per cycle - Use this injection only once during the same treatment cycle; using it again increases the chance of ovarian hyperstimulation.",
    fr: "Une seule fois par cycle - N'utilisez cette injection qu'une seule fois au cours d'un même cycle de traitement ; la répéter augmente le risque d'hyperstimulation ovarienne.",
  },
  sourceSegment:
    "⚠ Only once per cycle - Use this injection only once during the same treatment cycle; using it again increases the chance of ovarian hyperstimulation.",
};

/**
 * OHSS, by its symptoms, to the clinic.
 *
 * The leaflet's list under "Call your doctor right away", minus one item:
 * trouble breathing is routed to emergency below, as it is for every other
 * medication here, because a clinic number is not an answer to "I cannot
 * breathe". The leaflet adds that the symptoms may appear some days after the
 * injection, which matters more for this medication than any other — the
 * walk is over in ten minutes and the drug works for a week.
 */
const CALL_CLINIC: ProtocolWarning = {
  id: "elonva.call-clinic-right-away",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Call your clinic right away - Severe abdominal swelling or pain, nausea, vomiting, sudden weight gain, diarrhoea or decreased urine output can be signs of ovarian hyperstimulation, even some days after the injection.",
    fr: "Appelez votre centre sans attendre - Gonflement ou douleur sévère du ventre, nausées, vomissements, prise de poids soudaine, diarrhée ou diminution des urines peuvent être des signes d'hyperstimulation ovarienne, même quelques jours après l'injection.",
  },
  sourceSegment:
    "⚠ Call your clinic right away - Severe abdominal swelling or pain, nausea, vomiting, sudden weight gain, diarrhoea or decreased urine output can be signs of ovarian hyperstimulation, even some days after the injection.",
};

/** The same sentence every guided medication carries, routed the same way. */
const BREATHING_OR_ALLERGIC: ProtocolWarning = {
  id: "elonva.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
    fr: "Aide urgente - Des difficultés respiratoires ou des signes de réaction allergique nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
};

/**
 * Split by when each can happen. An allergic reaction is at the needle; OHSS
 * is days away, so it belongs with the card she reads last. Each warning sits
 * on one step only — the contract test holds warning ids unique per walk.
 */
const AT_THE_NEEDLE: ProtocolWarning[] = [BREATHING_OR_ALLERGIC];
const IN_THE_DAYS_AFTER: ProtocolWarning[] = [ONLY_ONCE_PER_CYCLE, CALL_CLINIC];

export const ELONVA_PROTOCOL: InjectionProtocol = {
  medicationSlug: "elonva",
  brands: ELONVA_BRANDS,
  // From the content's own alias list, minus the trade name it repeats.
  aliases: ["Corifollitropin alfa", "Corifollitropine alfa", "long-acting FSH"],
  indication: "stimulation",
  // Two syringes exist, 100 and 150 micrograms, chosen by weight and age before
  // the box is bought. Neither is dialled, so a record in anything else is a
  // record of a syringe that does not exist.
  dose: { unit: "mcg", strengths: [100, 150] },
  form: "pre_filled_syringe",
  prescriptionForms: ["pre_filled_syringe"],
  route: "subcutaneous",
  version: "2026-10-05.1",
  derivedFrom: "content/data/medications/gonadotropins.json#corifollitropin",
  steps: [
    {
      // The three reasons not to use the syringe are the leaflet's own list
      // under "Do not use Elonva", and one of them — out of the fridge for
      // more than a month — is invisible on the syringe. The storage note on
      // the carton is the only record, which is why it is said here and not
      // left to the factsheet.
      id: "elonva.syringe.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Wash your hands with soap and water and dry them. Check the expiry date on the carton. Do not use the syringe if the solution is not clear, if it has been out of the refrigerator for more than one month, or if the syringe or the needle is damaged. {brand} is a single injection on stimulation day 1 and is used only once in the same treatment cycle.",
        fr: "Préparer - Lavez-vous les mains avec de l'eau et du savon et séchez-les. Vérifiez la date de péremption sur la boîte. N'utilisez pas la seringue si la solution n'est pas limpide, si elle est restée hors du réfrigérateur plus d'un mois, ou si la seringue ou l'aiguille est abîmée. {brand} est une injection unique au premier jour de stimulation et ne s'utilise qu'une seule fois au cours d'un même cycle de traitement.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      // Bubbles are tapped up *before* the cap comes off, with the syringe
      // upright, and the needle goes on while it is still upright — the
      // leaflet's order, kept because the air has to be at the needle end
      // for the droplet step to push it out.
      id: "elonva.syringe.attach-the-needle",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Attach the needle - Hold the syringe with the grey cap pointing upwards and tap it gently with your finger so any air bubbles rise to the top. Keeping it upwards, unscrew the grey cap, then screw the needle shield with the needle inside onto the syringe. Pull the needle shield straight off and be careful with the needle. The syringe contains your full prescribed dose{dose}; you do not dial it.",
        fr: "Fixer l'aiguille - Tenez la seringue avec le bouchon gris vers le haut et tapotez-la doucement avec le doigt pour faire remonter les bulles d'air. En la gardant vers le haut, dévissez le bouchon gris, puis vissez sur la seringue le capuchon protecteur contenant l'aiguille. Retirez le capuchon protecteur verticalement et soyez prudente avec l'aiguille. La seringue contient votre dose complète prescrite{dose} ; vous ne la réglez pas.",
      },
    },
    {
      // `verify` with a confirmation: it is a look she has to take. Without
      // the droplet the air in the path goes in instead of the first part of
      // the dose, and nothing on the syringe or in her body says so.
      id: "elonva.syringe.check-for-a-droplet",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Check for a droplet - Hold the syringe upright between your index and middle finger, place your thumb on the plunger, and carefully push the plunger up until a tiny droplet appears at the tip of the needle.",
        fr: "Vérifier la goutte - Tenez la seringue vers le haut entre l'index et le majeur, placez le pouce sur le piston et poussez-le avec précaution jusqu'à ce qu'une petite goutte apparaisse à l'extrémité de l'aiguille.",
      },
    },
    {
      // The site is the leaflet's — just below the navel, 5 cm around the
      // point — and so is the minute. The content used to say "at least 5 cm
      // from the navel", which is a different place.
      id: "elonva.syringe.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean the site - Swab your abdomen just below your belly button with a disinfectant such as alcohol. Clean about 5 cm around the point where the needle will go in and let it dry for at least one minute.",
        fr: "Désinfecter le site - Nettoyez le ventre juste sous le nombril avec un désinfectant, par exemple de l'alcool. Nettoyez sur environ 5 cm autour du point d'injection et laissez sécher au moins une minute.",
      },
    },
    {
      // "Count to five" and "the needle withdraws automatically" are both the
      // leaflet's. The second is why this step does not end with "withdraw the
      // needle" like every other walk: there is nothing to withdraw.
      id: "elonva.syringe.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Pinch a fold of skin between your thumb and index finger. Insert the entire needle at a 90° angle into the fold. Carefully press the plunger until it cannot go further, hold it down and count to five so that all of the solution is injected. Release your thumb: the needle withdraws automatically into the syringe and locks. Do not inject into a muscle.",
        fr: "Injecter - Pincez un pli de peau entre le pouce et l'index. Introduisez l'aiguille entièrement dans le pli selon un angle de 90°. Poussez le piston avec précaution jusqu'à ce qu'il ne puisse pas aller plus loin, maintenez-le enfoncé et comptez jusqu'à cinq pour que toute la solution soit injectée. Relâchez le pouce : l'aiguille se rétracte automatiquement dans la seringue et se bloque. N'injectez pas dans un muscle.",
      },
      warnings: AT_THE_NEEDLE,
    },
    {
      id: "elonva.syringe.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Apply gentle pressure with gauze; do not rub. Dispose of the syringe in a sharps container, never in household waste. Do not use daily FSH during the seven days after this injection; your clinic will tell you when daily injections resume.",
        fr: "Après l'injection - Appliquez une légère pression avec une compresse ; sans frotter. Jetez la seringue dans un collecteur, jamais avec les ordures ménagères. N'utilisez pas de FSH quotidienne pendant les sept jours qui suivent cette injection ; votre centre vous dira quand reprendre les injections quotidiennes.",
      },
      warnings: IN_THE_DAYS_AFTER,
    },
  ],
};
